import React, { useEffect, useContext } from "react";
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import { api } from "common";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import moment from "moment/min/moment-with-locales";
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import CountrySelect from "../components/CountrySelect";
import PhoneIcon from "@material-ui/icons/Phone";
import styles from "assets/jss/material-kit-react/views/loginPage.js";
const useStyles = makeStyles(styles);

function AuthLoading(props) {
  const { t } = useTranslation();
  const {
    fetchUser,
    fetchCarTypes,
    fetchSettings,
    fetchBookings,
    fetchCancelReasons,
    fetchPromos,
    fetchDriverEarnings,
    fetchUsers,
    fetchNotifications,
    fetchEarningsReport,
    signOut,
    fetchWithdraws,
    fetchPaymentMethods,
    fetchLanguages,
    fetchWalletHistory,
    fetchCars,
    fetchComplain,
    fetchSMTP,
    fetchSos,
    fetchSMSConfig,
    countries,
  } = api;
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const languagedata = useSelector((state) => state.languagedata);
  const settingsdata = useSelector((state) => state.settingsdata);
  const [data, setData] = React.useState({
    country: null,
    mobile: "",
    selectedCountry: null,
  });
  const classes = useStyles();

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch, fetchSettings]);

  useEffect(() => {
    let resources = {};
    let defaultLang = {};
    if (languagedata.langlist) {
      for (const value of Object.values(languagedata.langlist)) {
        resources[value.langLocale] = value.keyValuePairs;
        if (value.default === true) {
          defaultLang = value;
          break;
        }
      }
      if (defaultLang && defaultLang.langLocale) {
        const stored = localStorage.getItem("lang");
        if (stored) {
          let langLocale = JSON.parse(stored).langLocale;
          let dateLocale = JSON.parse(stored).dateLocale;
          i18n.addResourceBundle(
            langLocale,
            "translations",
            resources[langLocale]
          );
          i18n.changeLanguage(langLocale);
          moment.locale(dateLocale);
        } else {
          i18n.addResourceBundle(
            defaultLang.langLocale,
            "translations",
            resources[defaultLang.langLocale]
          );
          i18n.changeLanguage(defaultLang.langLocale);
          moment.locale(defaultLang.dateLocale);
        }
      }
      dispatch(fetchUser());
    }
  }, [languagedata, dispatch, fetchUser]);

  useEffect(() => {
    if (settingsdata.settings) {
      dispatch(fetchLanguages());
      dispatch(fetchCarTypes());
      document.title = settingsdata.settings.appName;
    }
  }, [settingsdata.settings, dispatch, fetchLanguages, fetchCarTypes]);

  useEffect(() => {
    if (settingsdata.error) {
      document.title = "Setup";
    }
  }, [settingsdata.error, dispatch]);

  useEffect(() => {
    if (auth.profile) {
      if (auth.profile.usertype) {
        let roles = Array.isArray(auth.profile.usertype)
          ? auth.profile.usertype
          : [auth.profile.usertype];
        let matched = false;
        if (roles.includes("customer")) {
          dispatch(fetchBookings());
          dispatch(fetchWalletHistory());
          dispatch(fetchPaymentMethods());
          dispatch(fetchCancelReasons());
          dispatch(fetchUsers());
          matched = true;
        }
        if (roles.includes("driver")) {
          dispatch(fetchBookings());
          dispatch(fetchWithdraws());
          dispatch(fetchPaymentMethods());
          dispatch(fetchCars());
          dispatch(fetchWalletHistory());
          matched = true;
        }
        if (roles.includes("admin")) {
          dispatch(fetchUsers());
          dispatch(fetchBookings());
          dispatch(fetchPromos());
          dispatch(fetchDriverEarnings());
          dispatch(fetchNotifications());
          dispatch(fetchEarningsReport());
          dispatch(fetchCancelReasons());
          dispatch(fetchWithdraws());
          dispatch(fetchComplain());
          dispatch(fetchPaymentMethods());
          dispatch(fetchCars());
          dispatch(fetchSMTP());
          dispatch(fetchSMSConfig());
          dispatch(fetchSos());
          matched = true;
        }
        if (roles.includes("fleetadmin")) {
          dispatch(fetchUsers());
          dispatch(fetchBookings());
          dispatch(fetchDriverEarnings());
          dispatch(fetchCars());
          dispatch(fetchCancelReasons());
          matched = true;
        }
        if (roles.includes("agent")) {
          dispatch(fetchWalletHistory());
          dispatch(fetchPaymentMethods());
          matched = true;
        }
        if (!matched) {
          alert(t("not_valid_user_type"));
          dispatch(signOut());
        }
      } else {
        alert(t("user_issue_contact_admin"));
        dispatch(signOut());
      }
    }
  }, [
    auth.profile,
    dispatch,
    fetchBookings,
    fetchCancelReasons,
    fetchDriverEarnings,
    fetchEarningsReport,
    fetchNotifications,
    fetchPromos,
    fetchUsers,
    fetchWithdraws,
    signOut,
    fetchPaymentMethods,
    fetchWalletHistory,
    fetchCars,
    fetchComplain,
    fetchSMTP,
    fetchSos,
    fetchSMSConfig,
    t,
  ]);
  const onInputChange = (event) => {
    setData({ ...data, [event.target.id]: event.target.value });
  };
  const onCountryChange = (object, value) => {
    if (value && value.phone) {
      setData({ ...data, country: value.phone, selectedCountry: value });
    }
  };
  const onSubmit = (e) => {
    e.preventDefault();
    const uri = "+" + data.country + data.mobile;
    let encoded = "setup?mobile=" + encodeURIComponent(uri);
    window.open(encoded, "_self");
  };

  return settingsdata.loading ? (
    <CircularLoading />
  ) : settingsdata.settings ? (
    auth.loading || !languagedata.langlist ? (
      <CircularLoading />
    ) : (
      props.children
    )
  ) : (
    <div>
      <div className={classes.container}>
        <GridContainer justify="center">
          <GridItem xs={12} sm={12} md={4}>
            <Card>
              <CardHeader color="info" className={classes.cardHeader}>
                <h4>{"Admin and Database Setup"}</h4>
              </CardHeader>
              <CardBody>
                <CountrySelect
                  countries={countries}
                  label={"Select Country"}
                  value={data.selectedCountry}
                  onChange={onCountryChange}
                  style={{ paddingTop: 20 }}
                  disabled={data.verificationId ? true : false}
                />
                <CustomInput
                  labelText={"Mobile No"}
                  id="mobile"
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    required: true,
                    endAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon className={classes.inputIconsColor} />
                      </InputAdornment>
                    ),
                  }}
                  onChange={onInputChange}
                  value={data.mobile}
                />
              </CardBody>
              <CardFooter className={classes.cardFooter}>
                <Button
                  className={classes.normalButton}
                  simple
                  color="primary"
                  size="lg"
                  onClick={onSubmit}
                >
                  {t("submit")}
                </Button>
              </CardFooter>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    </div>
  );
}

export default AuthLoading;
